import { Injectable } from '@angular/core';

import { addDays } from 'date-fns';
import { filter } from 'rxjs';

import { Store } from '@ngrx/store';

import { selectChatState, selectUiState, selectUser } from '../../../store/app.selectors';

import { EpochTime } from '../../../core/interfaces/IEpochTime';
import { findOwnerUser, getTagValue } from '../../../core/utils/owner-match';
import {
  setChannelLoaded,
  setShowChannel,
  setShowChannels,
  setShowProfile,
  setShowScrollButton,
  setShowStatusBanner,
} from '../../shared/store/ui.actions';
import { IUserProfile } from '../../user/interfaces/IUserProfile';
import {
  ChannelConsent,
  ChannelServiceType,
  ChannelStatus,
  ChannelType,
  IBulkLoadResponse,
  IChannel,
  IChannelByParams,
} from '../interfaces/IChannel';
import { DealerSettingName } from '../interfaces/IDealerSettings';
import { DeliveryStatusTypesOptions, IMessage } from '../interfaces/IMessage';
import { IMessageableEntity } from '../interfaces/IMessageableEntity';
import { IFilters, IWorkspace, WorkspaceFeatures } from '../interfaces/IWorkspace';
import {
  changeChannelOrderId,
  changeChannelOwner,
  getChannelLang,
  getChannelMembersPhotos,
  getMessagesTemplates,
  getSmartReplies,
  getStandardReplies,
  selectChannel,
  setChannelConsent,
  setChannelExpirationDate,
  setChannelInfo,
  setChannelOrderId,
  setChannelOwner,
  setChannelStatus,
  setChannelStatusEnabled,
  setLoadingOldMessages,
  setLoadingSmartReplies,
  setLoadingStandardReplies,
  toggleSidebar,
} from '../store/chat.actions';
import { AppStateWithChat } from '../store/chat.reducer';
import { MessagesService } from './messages.service';
import { PermissionsService } from './permissions.service';
import { PhonesService } from './phones.service';

@Injectable()
export class ChannelsService {
  showBackArrow: boolean;
  selectedWorkSpace: IWorkspace | null = null;
  user: IUserProfile | null = null;
  messagesFilter: { size: number };
  showChannels: boolean;
  isLoadingUsers: boolean;
  channels: IChannel[];
  selectedChannel: IChannel | null = null;
  users: IMessageableEntity[];
  ownerId: string | undefined;
  tokenAuthentication: string | null = null;
  constructor(
    private store: Store<AppStateWithChat>,
    private permissionsService: PermissionsService,
    private messagesService: MessagesService,
    private phonesService: PhonesService
  ) {
    this.setUserSubscription();
    this.getUiSubscriptions();
    this.setChatSubscription();
  }

  setUserSubscription(): void {
    this.store.select(selectUser).subscribe((user) => {
      this.user = user;
      this.ownerId = this.user?.subjectMessageableEntity?.businessReference?.entityUID;
    });
  }

  getUiSubscriptions() {
    this.store.select(selectUiState).subscribe(({ showBackArrow, showChannels, isLoadingUsers }) => {
      this.showBackArrow = showBackArrow;
      this.showChannels = showChannels;
      this.isLoadingUsers = isLoadingUsers;
    });
  }

  setChatSubscription(): void {
    this.store
      .select(selectChatState)
      .pipe(filter((data) => data !== undefined))
      .subscribe(({ selectedWorkSpace, messagesFilter, tokenAuthentication, channels, users, selectedChannel }) => {
        this.selectedWorkSpace = selectedWorkSpace;
        this.messagesFilter = messagesFilter;
        this.channels = channels;
        this.selectedChannel = selectedChannel;
        this.users = users;
        this.tokenAuthentication = tokenAuthentication;
      });
  }

  selectChannel(channel: IChannel): void {
    if (channel) {
      channel = this.setBlockedWhatsapp(channel);
      this.store.dispatch(selectChannel({ channel }));
      this.store.dispatch(setShowStatusBanner({ value: true }));
      this.store.dispatch(setLoadingOldMessages({ channelUID: channel.uid, value: false }));
      this.store.dispatch(setChannelLoaded({ value: true }));
      this.store.dispatch(setShowScrollButton({ value: false }));
      this.store.dispatch(getChannelLang({ channel }));
      this.store.dispatch(setLoadingSmartReplies({ value: true, channelUID: channel.uid }));
      this.store.dispatch(getSmartReplies({ channel }));
      this.store.dispatch(setLoadingStandardReplies({ value: true, channelUID: channel.uid }));
      this.store.dispatch(getStandardReplies({ channel }));
      this.store.dispatch(getChannelMembersPhotos({ channel }));
      this.store.dispatch(getMessagesTemplates({ channel }));
      if (this.showBackArrow) {
        this.store.dispatch(setShowProfile({ value: false }));
        this.store.dispatch(setShowChannels({ value: false }));
        this.store.dispatch(setShowChannel({ value: true }));
      }
    }
  }

  openChannelInfo(): void {
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.startConversationInfoOpen])) {
      this.store.dispatch(toggleSidebar({ active: true }));
    }
  }

  autoSelectChannel(channels: IChannel[], channelID: string, phoneNumber: string, routing: ChannelServiceType): void {
    if (!routing) {
      routing = 'SMS';
    }
    phoneNumber = this.phonesService.setPhoneNumber(phoneNumber);
    let channel = channels[0];
    if (channelID || phoneNumber) {
      channel =
        channels.find((e) => e.uid === channelID || (e.phoneNumber === phoneNumber && e.routing === routing)) || channels[0];
    }
    if (channel) {
      this.selectChannel(channel);
      this.openChannelInfo();
    }
  }

  filterWhatsappChannels(channels: IChannel[]): IChannel[] {
    if (!this.selectedWorkSpace || (this.selectedWorkSpace.id !== 'sales' && this.selectedWorkSpace.id !== 'service')) {
      return channels;
    }
    return [...channels].filter((channel) => {
      if (channel.routing.toUpperCase() === 'WHATSAPP') {
        return this.permissionsService.checkCanUseWhatsapp();
      } else {
        return true;
      }
    });
  }

  sortChannels(channelsList: IChannel[]): IChannel[] {
    const channels = [...channelsList].sort((chat1, chat2) => {
      if (chat1.pinned && !chat2.pinned) {
        return -1;
      } else if (!chat1.pinned && chat2.pinned) {
        return 1;
      } else {
        return new Date(chat2.lastMessage?.createdAt ?? 0).getTime() - new Date(chat1.lastMessage?.createdAt ?? 0).getTime();
      }
    });
    return channels;
  }

  setChannelMessagingApi(member: IBulkLoadResponse, channelByParams: IChannelByParams | null = null): IChannel {
    let messages = member?.messages?.length
      ? member.messages.map((message) => this.messagesService.setMessagingApiMessage(message))
      : [];
    messages = this.messagesService.sortMessages(messages);
    if (member?.channel?.routing) {
      member.channel.routing = member.channel.routing.toUpperCase() as ChannelServiceType;
    }
    const channel: IChannel = {
      ...member.channel,
      type: member.channel.type,
      displayName: member.channel?.displayName.trim(),
      messages: messages || [],
      totalMembersCount: member.totalCount,
      members: [],
      expireAt: this.epochToDate(member.channel.expireAt as EpochTime) ?? undefined,
      to: member.to,
      unreadMessages: member.unreadMessages,
      isVisible: member.visible,
      standardResponses: [],
      smartReplies: [],
      isMuted: member.muted,
      pinned: member.pinned,
      unread: member.unread,
      owner: undefined,
      isBotMode: member.channel.handOffMode === 'AI',
      lastMessage: messages?.length
        ? messages[messages.length - 1]
        : {
            outcoming: true,
            body: 'Channel created',
            uid: '',
            createdBy: this.user!.subjectMessageableEntity,
            createdAt: new Date(member.createdAt ?? Date.now()),
            deliveryStatus: DeliveryStatusTypesOptions.delivered,
          },
      enabled: member.enabled,
      messagesCompleted: messages?.length < 20,
      member: {
        displayName: member.displayName,
        businessReference: member.businessReference,
        uid: member.uid,
        role: member.role,
      },
      lang: member?.channel?.lang,
      phoneNumber: member?.to?.phone?.e164,
      routing: member?.channel?.routing || this.selectedWorkSpace?.defaultTypeService || 'SMS',
    };
    if (channel.isBotMode) {
      channel.enabled = false;
    }
    if (channel.type === ChannelType.DIRECT_MESSAGE) {
      channel.members = channel.members ?? [];
      channel.members.push(member.to);
      channel.members.push(member.from);
      if (!member.channel.displayName.trim() || member.channel.displayName.toUpperCase().trim() === 'N/A') {
        if (member?.displayName) {
          channel.displayName = member.displayName.trim();
        }
      }
      if (
        this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.assignOrphanDisplayName]) &&
        !channel.displayName
      ) {
        channel.displayName = 'Orphan message';
      }
      channel.imageURL = member?.to?.profileImageURL;
    }
    if (channelByParams) {
      channel.phoneNumber = channelByParams.phoneNumber;
      if (channelByParams.orderId) {
        channel.systemTags = member.channel?.systemTags?.length ? member.channel?.systemTags : [];
        channel.systemTags.push(`newOrderId:${channelByParams.orderId}`);
      }
    }
    if (
      this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelPhoneNumberFromTo]) &&
      member?.to?.phone?.e164
    ) {
      channel.phoneNumber = this.phonesService.setPhoneNumber(member.to.phone.e164);
    }
    if (channelByParams?.messagesTags) {
      channel.messagesTags = channelByParams.messagesTags;
    }
    return channel;
  }

  updateChannelOrderID(channel: IChannel): void {
    const newOrderId = this.getTagValueByParam(channel, 'newOrderId') as string;
    const orderId = this.getTagValueByParam(channel, 'orderId') as string;
    if (newOrderId && orderId !== newOrderId) {
      this.store.dispatch(changeChannelOrderId({ channel, orderId: newOrderId }));
    }
  }

  updateChannelByParams(channel: IChannel, channelByParams: IChannelByParams): void {
    const newChannel = { ...channel };
    if (channelByParams.displayName) {
      newChannel.standardResponses = [];
      newChannel.displayName = channelByParams.displayName;
    }
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelOwnerByUserId])) {
      if (this.user?.subjectMessageableEntity?.businessReference?.entityUID) {
        this.store.dispatch(
          changeChannelOwner({
            channel: newChannel,
            owner: {
              id: this.user.subjectMessageableEntity.businessReference.entityUID,
              displayName: this.user.subjectMessageableEntity.displayName,
            },
          })
        );
      }
    }

    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelOwnerByTag])) {
      this.store.dispatch(
        changeChannelOwner({
          channel: newChannel,
          owner: {
            id: channelByParams.ownerId ?? '',
            displayName: channelByParams.ownerDisplayName ?? '',
          },
        })
      );
    }

    this.store.dispatch(
      setChannelInfo({
        channel: newChannel,
        displayName: channelByParams.displayName ?? '',
      })
    );
  }

  checkChannelNewOrderID(channel: IChannel, newMessage: IMessage): void {
    const orderChannelID = this.getTagValueByParam(channel, 'orderId') as string;
    const orderMessageID = this.getTagValueByParam(newMessage, 'orderId') as string;
    if (orderMessageID && orderMessageID !== orderChannelID) {
      this.store.dispatch(setChannelOrderId({ channel, orderId: orderMessageID }));
    }
  }

  epochToDate(epochTime: EpochTime): Date | null {
    if (epochTime) {
      const timeTotal = epochTime._seconds * 1000 + Math.floor(epochTime._nanoseconds / 1000000);
      return new Date(timeTotal);
    } else {
      return null;
    }
  }

  changeWhatsappStatus(channel: IChannel): void {
    if (channel.routing === 'WHATSAPP') {
      this.store.dispatch(setChannelStatusEnabled({ channelUID: channel.uid, value: true }));
      this.store.dispatch(setChannelConsent({ channelUID: channel.uid, value: ChannelConsent.GRANTED }));
      const expirationDate = addDays(new Date(), 1);
      this.store.dispatch(setChannelExpirationDate({ channelUID: channel.uid, expirationDate }));
    }
  }

  changeChannelStatus(channel: IChannel, value: ChannelStatus): void {
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canChangeConversationStatus])) {
      this.store.dispatch(
        setChannelStatus({
          channel,
          value,
        })
      );
    }
  }

  checkWhatsappBlocked(channel: IChannel): boolean {
    if ((channel.routing.toUpperCase() as ChannelServiceType) === 'WHATSAPP') {
      if (!channel.messages.length || !channel.expireAt) {
        return true;
      }
      return (channel.expireAt as Date) < new Date();
    } else {
      return false;
    }
  }

  setBlockedWhatsapp(channel: IChannel): IChannel {
    if (!this.checkWhatsappBlocked(channel)) {
      return channel;
    }
    return { ...channel, enabled: false, consentStatus: ChannelConsent.REACTIVATE };
  }

  setPhoneChannelResponse(channel: IChannel, data: IChannelByParams): IChannel {
    if (!this.user) {
      throw new Error('User profile is required to create phone channel response');
    }
    const newChannel: IChannel = {
      type: ChannelType.DIRECT_MESSAGE,
      uid: channel.uid,
      createdAt: new Date(),
      displayName: data.displayName ?? '',
      enabled: data.routing === 'WHATSAPP' ? false : true,
      isMember: true,
      isPrivate: false,
      member: { ...this.user.subAccountMessageableEntity },
      routing: data.routing,
      messages: [],
      features: [],
      systemTags: channel.systemTags,
      phoneNumber: data.phoneNumber,
      lastMessage: {
        outcoming: false,
        body: 'Channel created',
        uid: '',
        createdBy: { displayName: '', uid: '' },
        createdAt: new Date(),
        deliveryStatus: DeliveryStatusTypesOptions.delivered,
      },
      owner: undefined,
      messagesTags: data.tags,
      consentStatus: data.routing === 'WHATSAPP' ? ChannelConsent.REACTIVATE : undefined,
    };
    newChannel.member.uid = newChannel.member.uid + '-' + newChannel.uid;
    return newChannel;
  }

  applyManualChannelsFilters(channels: IChannel[], filters: IFilters[]): IChannel[] {
    if (filters?.length) {
      const manuals = filters.filter((filter) => filter.isManualFilter && filter.value);
      if (manuals.length) {
        for (const manual of manuals) {
          channels = [...channels].filter((channel) => {
            if (manual.comparationType === 'mayor' && manual.comparationValue != null) {
              return (channel[manual.field as keyof IChannel] as number) > Number(manual.comparationValue);
            }
            if (manual.comparationType === 'equal') {
              return channel[manual.field as keyof IChannel] === manual.value;
            }
            return true;
          });
        }
      }
    }
    return channels;
  }

  isFilterChannel(channel: IChannel, filters: IFilters[]): boolean {
    let response = true;
    if (!this.permissionsService.checkCanUseWhatsapp() && channel.routing === 'WHATSAPP') {
      return false;
    }
    if (filters?.length) {
      const activeFilters = filters.filter((e) => this.checkFiltersChannels(e)).filter((filter) => filter.value);
      if (activeFilters.length) {
        for (const filter of activeFilters) {
          if (!filter.isTagFilter) {
            if (filter.comparationType === 'mayor' && response && filter.comparationValue != null) {
              response =
                (channel[(filter.fieldLocalFilter || filter.field) as keyof IChannel] as number) >
                Number(filter.comparationValue);
            }
            if (filter.comparationType === 'equal' && response) {
              // rare case routing
              if (filter.field === 'routing' && this.isSMSandWhatsappActive(activeFilters)) {
                response = channel.routing.toUpperCase() === 'WHATSAPP' || channel.routing.toUpperCase() === 'SMS';
              } else {
                response = channel[(filter.fieldLocalFilter || filter.field) as keyof IChannel] === filter.value;
              }
            }
            if (filter.comparationType === 'array' && response) {
              const values = activeFilters.filter((e) => e.field === filter.field).map((e) => e.value);
              response = values.includes(channel[(filter.fieldLocalFilter || filter.field) as keyof IChannel] as string);
            }
          } else {
            if (channel?.systemTags?.length) {
              if (filter.comparationType === 'equal' && response) {
                response = channel.systemTags.some(
                  (e) => e === `${filter.field}:${this[filter.field as keyof ChannelsService]}`
                );
              }
            }
          }
        }
      }
    }
    return response;
  }

  checkFiltersChannels(filter: IFilters): boolean {
    if (!filter?.dealerSettings?.length) {
      return true;
    }
    if (this.selectedWorkSpace?.id === 'sales' && filter.dealerSettings.includes(DealerSettingName.WhatsAppSalesModule)) {
      return this.permissionsService.checkDealerSetting(DealerSettingName.WhatsAppSalesModule);
    }
    if (
      this.selectedWorkSpace?.id === 'service' &&
      filter.dealerSettings.includes(DealerSettingName.WhatsAppServiceModule)
    ) {
      return this.permissionsService.checkDealerSetting(DealerSettingName.WhatsAppServiceModule);
    }
    return this.permissionsService.checkDealerSetting(filter.dealerSettings[0]);
  }

  isSMSandWhatsappActive(filters: IFilters[]): boolean {
    const whatsappFilter = filters.find((e) => e.name.toUpperCase() === 'WHATSAPP');
    const smsFilter = filters.find((e) => e.name.toUpperCase() === 'SMS');
    return !!whatsappFilter?.value && !!smsFilter?.value;
  }

  createChannelUrl(channel: IChannel, workspace: IWorkspace, baseUrl: string, hideToken: boolean): string {
    let url = `${baseUrl}#/${workspace?.shortVersion}/channel`;
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canUseChannelUIDonURL])) {
      url = url + '/' + channel?.uid;
    }
    url = url + '?';
    if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.useRoutingUrl])) {
      if (channel?.phoneNumber) {
        url = `${url}&phoneNumber=${channel.phoneNumber}`;
      }
      if (channel?.routing) {
        url = `${url}&routing=${channel.routing}`;
      }
    }
    if (!hideToken) {
      const tkn = localStorage.getItem('messaging-token');
      url = `${url}&tkn=${tkn}`;
    }

    if (this.tokenAuthentication) {
      url = `${url}&authorization=${this.tokenAuthentication}`;
    }
    return url;
  }

  setDatesMessages(channel: IChannel, channelByParams: IChannelByParams): IChannel {
    if (channelByParams) {
      if (
        channelByParams?.startDateTime &&
        this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canUseOldMessagesBanner])
      ) {
        channel.messagesStartDate = channelByParams.startDateTime as Date;
      }
      if (
        channelByParams?.endDateTime &&
        this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.canUseLastMessagesBanner])
      ) {
        channel.messagesEndDate = channelByParams.endDateTime as Date;
      }
    }

    return channel;
  }

  // channels ownership

  setChannelsOwnership(channels: IChannel[], users: IMessageableEntity[]): IChannel[] {
    for (let index = 0; index < channels.length; index++) {
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelOwnerByUserId]) && users.length) {
        channels[index] = this.setChannelOwnerByUserID(channels[index], users);
      }
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelOwnerByTag])) {
        channels[index] = this.setChannelOwnerByTag(channels[index]);
      }
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setMessageOwnerByTag])) {
        channels[index].messages = this.messagesService.setMessagesOwnersByTag(channels[index].messages);
      }
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setMessageOwnerByUserId])) {
        channels[index].messages = this.messagesService.setMessagesOwnersByUserID(channels[index].messages, users);
      }
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setMessageOwnerByCreatedBy])) {
        channels[index].messages = this.messagesService.setMessagesOwnersByCreatedBy(channels[index].messages);
      }
    }
    return channels;
  }

  setChannelOwnerByUserID(channel: IChannel, users: IMessageableEntity[]): IChannel {
    const ownerId = this.getTagValueByParam(channel, 'ownerId');
    if (ownerId) {
      const user = findOwnerUser(users, ownerId);
      if (user) {
        channel = {
          ...channel,
          owner: {
            displayName: getTagValue(channel.systemTags, 'ownerDisplayName') || user?.displayName,
            id: ownerId as string,
            profileImageURL: user?.profileImageURL,
          },
        };
      }
    }
    const awayOwnerId = this.getTagValueByParam(channel, 'originalOwnerId');
    if (awayOwnerId) {
      if (awayOwnerId !== ownerId) {
        const awayUser = findOwnerUser(users, awayOwnerId);
        if (awayUser) {
          channel = {
            ...channel,
            awayOwner: {
              displayName: getTagValue(channel.systemTags, 'originalOwnerDisplayName') || awayUser?.displayName,
              id: awayOwnerId as string,
              profileImageURL: awayUser?.profileImageURL,
            },
          };
        }
      }
    }
    return channel;
  }

  setChannelOwnerByTag(channel: IChannel): IChannel {
    const ownerId = this.getTagValueByParam(channel, 'ownerId') as string;
    if (ownerId) {
      const ownerDisplayName = this.getTagValueByParam(channel, 'ownerDisplayName') as string;
      const user = findOwnerUser(this.users, ownerId);
      channel.owner = {
        displayName: ownerDisplayName || user?.displayName || '',
        id: ownerId,
        profileImageURL: user?.profileImageURL,
      };
    }
    const awayOwnerId = this.getTagValueByParam(channel, 'originalOwnerId') as string;
    if (awayOwnerId) {
      if (awayOwnerId !== ownerId) {
        const awayOwnerDisplayName = this.getTagValueByParam(channel, 'originalOwnerDisplayName') as string;
        const awayUser = findOwnerUser(this.users, awayOwnerId);
        channel.awayOwner = {
          displayName: awayOwnerDisplayName || awayUser?.displayName || '',
          id: awayOwnerId,
          profileImageURL: awayUser?.profileImageURL,
        };
      }
    }
    return channel;
  }

  changeChannelOwner(channel: IChannel, newOwnerId: string, newOwnerDisplayName: string, force = false): void {
    if (this.permissionsService.checkDealerSetting(DealerSettingName.channelOwnerAutoReassignEnabled) || force) {
      const ownerId = this.getTagValueByParam(channel, 'ownerId');
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelOwnerByTag])) {
        const ownerDisplayName = this.getTagValueByParam(channel, 'ownerDisplayName');
        if (newOwnerId && (newOwnerId !== ownerId || newOwnerDisplayName !== ownerDisplayName))
          this.store.dispatch(changeChannelOwner({ channel, owner: { id: newOwnerId, displayName: newOwnerDisplayName } }));
      }
      if (this.permissionsService.checkWorkspaceFeatures([WorkspaceFeatures.setChannelOwnerByUserId])) {
        if (newOwnerId && newOwnerId !== ownerId) {
          const user = findOwnerUser(this.users, newOwnerId);
          this.store.dispatch(
            changeChannelOwner({
              channel,
              owner: { displayName: user?.displayName ?? '', id: newOwnerId, profileImageURL: user?.profileImageURL },
            })
          );
        }
      }
    }
  }

  checkChannelNewOwner(channel: IChannel, newMessage: IMessage): void {
    if (this.permissionsService.checkDealerSetting(DealerSettingName.channelOwnerAutoReassignEnabled)) {
      const ownerChannelID = this.getTagValueByParam(channel, 'ownerId') as string;
      const ownerMessageID = this.getTagValueByParam(newMessage, 'ownerId') as string;
      const ownerMessageDisplayName = this.getTagValueByParam(newMessage, 'ownerDisplayName');
      if (ownerMessageID && ownerMessageDisplayName && ownerChannelID !== ownerMessageID) {
        if (
          this.permissionsService.checkWorkspaceFeatures([
            WorkspaceFeatures.setChannelOwnerByUserId,
            WorkspaceFeatures.setChannelOwnerByTag,
          ])
        ) {
          this.store.dispatch(
            setChannelOwner({
              channel,
              owner: {
                displayName: ownerMessageDisplayName as string,
                id: ownerMessageID as string,
                profileImageURL: undefined,
              },
              tags: this.modifyTagsOwner(channel, ownerMessageID, ownerMessageDisplayName as string),
            })
          );
        }
      }
    }
  }

  modifyTagsOwner(channel: IChannel, ownerId: string, ownerDisplayName: string): string[] {
    if (!channel?.systemTags?.length) {
      return ['ownerId:' + ownerId, 'ownerDisplayName:' + ownerDisplayName];
    }
    let tags = [...channel.systemTags];
    tags = tags.map((e) => {
      if (e.includes('ownerId')) {
        e = 'ownerId:' + ownerId;
      }
      if (e.includes('ownerDisplayName')) {
        e = 'ownerDisplayName:' + ownerDisplayName;
      }
      return e;
    });
    return tags;
  }

  getTagValueByParam(channel: IChannel | IMessage, param: string): string | number | null {
    if (channel?.systemTags?.length) {
      const tag = channel.systemTags.find((tag) => {
        const result = tag.split(':')[0];
        return result === param;
      });
      if (tag) {
        const tagSplited = tag.split(`${param}:`);
        const value = tagSplited[1];
        return value;
      }
    }
    return null;
  }
}
